import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Builds the loading shimmer: a blurred white ribbon drawn as a chain of cubic bezier curves
 * running down an 80×300 box, with a random number of bends of uneven length and swing, so no two
 * variant switches look alike. Each curve's first control point mirrors the previous curve's last
 * one through their shared point (scaled to the new curve's length), so the bends join smoothly.
 *
 * @returns {string} A CSS url() holding the SVG as a data URI.
 */
function createShimmerWave() {
  const width = 80;
  const height = 300;
  const center = width / 2;
  const random = (/** @type {number} */ min, /** @type {number} */ max) => min + Math.random() * (max - min);

  const maxSwing = 24;
  const bends = Math.round(random(5, 7));
  const weights = Array.from({ length: bends }, () => random(0.7, 1.3));
  const total = weights.reduce((sum, weight) => sum + weight, 0);

  let y = 0;
  let x = center;
  let side = Math.random() < 0.5 ? 1 : -1;
  /** @type {{ x: number, y: number } | undefined} */
  let previousControl;
  let previousLength = 0;
  let path = `M${x} ${y}`;

  for (const weight of weights) {
    const length = (weight / total) * height;
    const nextX = center + random(-4, 4);
    const nextY = y + length;

    // The mirror keeps the join smooth; capping its reach stops a long bend after a short one
    // from swinging past the edge of the box.
    const mirrorScale = previousControl
      ? Math.min(length / previousLength, maxSwing / Math.max(Math.abs(x - previousControl.x), 1))
      : 0;
    const control1 = previousControl
      ? {
          x: x + (x - previousControl.x) * mirrorScale,
          y: y + (y - previousControl.y) * mirrorScale,
        }
      : { x: center + side * random(14, maxSwing), y: y + length * 0.25 };
    const control2 = { x: center + side * random(14, maxSwing), y: nextY - length * 0.25 };

    path += `C${control1.x.toFixed(1)} ${control1.y.toFixed(1)} ${control2.x.toFixed(1)} ${control2.y.toFixed(1)} ${nextX.toFixed(1)} ${nextY.toFixed(1)}`;

    previousControl = control2;
    previousLength = length;
    x = nextX;
    y = nextY;
    side *= -1;
  }

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" fill="none">` +
    '<filter id="w" x="-50%" y="-10%" width="200%" height="120%"><feGaussianBlur stdDeviation="3"/></filter>' +
    `<path d="${path}" stroke="#fff" stroke-opacity="0.75" stroke-width="13" stroke-linecap="round" filter="url(#w)"/>` +
    '</svg>';

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [slideAnnouncement] - Visually hidden live region for slide changes.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelected, { signal });

    const rail = this.querySelector('slideshow-controls:has(.slideshow-controls__rail-arrow)');
    const thumbnailList = rail?.querySelector('.slideshow-controls__thumbnails');
    if (rail && thumbnailList) {
      this.#railObserver = new ResizeObserver(() => this.#updateRailOverflow(rail, thumbnailList));
      this.#railObserver.observe(rail);
      this.#railObserver.observe(thumbnailList);
    }
  }

  /** @type {ResizeObserver | undefined} */
  #railObserver;

  /**
   * The rail arrows only show when the thumbnails don't all fit (ticket 1623). The list is compared
   * with the whole rail, not the scroll area, so showing or hiding the arrows can't flip the result.
   * The rail is vertical on desktop and horizontal from 750 to 1023px, so measure along its axis.
   * @param {HTMLElement} rail - The thumbnails slideshow-controls.
   * @param {HTMLElement} thumbnailList - The full-size list of thumbnails.
   */
  #updateRailOverflow(rail, thumbnailList) {
    const horizontal = getComputedStyle(rail).flexDirection === 'row';
    const overflows = horizontal
      ? thumbnailList.offsetWidth > rail.clientWidth + 1
      : thumbnailList.offsetHeight > rail.clientHeight + 1;

    rail.toggleAttribute('data-rail-overflow', overflows);
  }

  #controller = new AbortController();

  /** @type {number | undefined} */
  #busyTimeout;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#railObserver?.disconnect();
    clearTimeout(this.#busyTimeout);
  }

  /**
   * Marks the gallery busy while the variant picker fetches the new section, which can take a
   * second or more. The busy state is never cleared on success: the variant update replaces this
   * whole element with a fresh one. The timeout only covers a fetch that fails silently.
   */
  #handleVariantSelected = () => {
    // Opt-in per block: the "Loading animation on variant change" setting renders data-variant-loading.
    if (!this.hasAttribute('data-variant-loading')) return;

    // A new random wave, direction and speed for the loading shimmer on every switch; thumbnails
    // ripple down the strip. The new gallery arrives without these inline styles.
    this.style.setProperty('--shimmer-wave', createShimmerWave());
    this.style.setProperty('--shimmer-angle', `${Math.round(Math.random() * 360)}deg`);
    this.style.setProperty('--shimmer-duration', `${(1 + Math.random() * 0.6).toFixed(2)}s`);
    this.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumbnail, index) => {
      if (thumbnail instanceof HTMLElement) thumbnail.style.setProperty('--shimmer-delay', `${index * 0.08}s`);
    });

    this.setAttribute('aria-busy', 'true');
    clearTimeout(this.#busyTimeout);
    this.#busyTimeout = window.setTimeout(() => this.removeAttribute('aria-busy'), 10000);
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Lets the new gallery fade in from the busy state instead of popping in.
    if (this.getAttribute('aria-busy') === 'true') newMediaGallery.setAttribute('data-variant-swapped', '');

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Keeps only the current slide's zoom button in the tab order: the other slides are aria-hidden,
   * so a focusable button inside them would be announced as nothing.
   * @param {SlideshowSelectEvent} event - The slideshow:select event.
   */
  #handleSlideSelected = (event) => {
    for (const slide of this.slideshow?.slides ?? []) {
      const button = slide.querySelector('.product-media-container__zoom-button');
      if (button instanceof HTMLElement) button.tabIndex = slide === event.detail.slide ? 0 : -1;
    }

    this.#updateRailArrows();

    // Announce arrow, swipe and thumbnail changes, but not the silent sync while the zoom dialog is open.
    const { slideAnnouncement, zoomDialogComponent } = this.refs;
    if (!slideAnnouncement || !event.detail.userInitiated || zoomDialogComponent?.refs.dialog.open) return;

    slideAnnouncement.textContent = (slideAnnouncement.dataset.template ?? '').replace(
      '[index]',
      `${event.detail.index + 1}`
    );
  };

  /**
   * The up/down arrows around the thumbnail rail (slideshow-controls `rail_arrows`). At the first/last
   * image they get aria-disabled rather than `disabled`, so a keyboard user who reaches the end keeps
   * focus on the arrow instead of being dropped to the page.
   */
  #updateRailArrows() {
    const { slideshow } = this;
    if (!slideshow) return;

    for (const [selector, atEnd] of /** @type {const} */ ([
      ['.slideshow-controls__rail-arrow--previous', slideshow.atStart],
      ['.slideshow-controls__rail-arrow--next', slideshow.atEnd],
    ])) {
      const arrow = this.querySelector(selector);
      if (!arrow) continue;

      if (atEnd) arrow.setAttribute('aria-disabled', 'true');
      else arrow.removeAttribute('aria-disabled');
    }
  }

  /**
   * Rail arrow: previous image. A no-op on the first image.
   * @param {Event} event - The click event.
   */
  showPrevious(event) {
    if (event.target instanceof Element && event.target.getAttribute('aria-disabled') === 'true') return;
    this.slideshow?.previous(event);
  }

  /**
   * Rail arrow: next image. A no-op on the last image.
   * @param {Event} event - The click event.
   */
  showNext(event) {
    if (event.target instanceof Element && event.target.getAttribute('aria-disabled') === 'true') return;
    this.slideshow?.next(event);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
